import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	IconButton,
	Divider,
	Paper,
	Chip
} from "@material-ui/core";
import {
	Close as CloseIcon,
	Warning as WarningIcon,
	VerifiedUser as VerifiedUserIcon,
	ErrorOutline as ErrorOutlineIcon,
	Info as InfoIcon,
	Block as BlockIcon
} from "@material-ui/icons";
import { withStyles, Theme } from "@material-ui/core/styles";

interface ExistsOwnMemberModelProps {
	onClose: () => void;
	existingAltId?: string;
	employeeName?: string;
	isSSN?: boolean; // Add this prop to differentiate
}

const StyledDialog = withStyles((theme: Theme) => ({
	root: {
		"& .MuiDialog-paper": {
			borderRadius: 12,
			minWidth: 480,
			maxWidth: 520,
			boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
		}
	}
}))(Dialog);

const WarningIconWrapper = withStyles((theme: Theme) => ({
	root: {
		backgroundColor: "#FEF3C7",
		borderRadius: "50%",
		padding: 16,
		marginBottom: 16,
		"& svg": {
			fontSize: 40,
			color: "#D97706"
		}
	}
}))(Box);

const ExistsOwnMemberModel: React.FC<ExistsOwnMemberModelProps> = ({
	onClose,
	existingAltId = "",
	employeeName = "",
	isSSN = false // Default to false (ALT ID mode)
}) => {
	const [isHovered, setIsHovered] = useState(false);

	// Determine if we're showing SSN or ALT ID
	const displayValue = existingAltId || "N/A";
	const idType = isSSN ? "SSN" : "Alt ID";
	const idLabel = isSSN ? "Detected SSN" : "Detected Alt ID";

	return (
		<StyledDialog
			open={true}
			onClose={onClose}
			aria-labelledby="existing-employee-dialog-title"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle
				id="existing-employee-dialog-title"
				style={{
					padding: "20px 24px 0 24px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center"
				}}
			>
				<Typography
					variant="h6"
					style={{
						fontWeight: 600,
						color: "#1F2937",
						fontSize: "1.25rem"
					}}
				>
					Existing Employee Detected
				</Typography>
			</DialogTitle>

			<DialogContent style={{ padding: "24px" }}>
				<Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
					{/* Status Chip */}
					<Chip
						icon={<BlockIcon style={{ fontSize: 16 }} />}
						label="Action Required"
						style={{
							backgroundColor: "#FEF3C7",
							color: "#92400E",
							fontWeight: 600,
							marginBottom: 16,
							border: "1px solid #FCD34D",
							padding: "4px 8px"
						}}
					/>

					{/* Main Message Paper */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#FFFBEB",
							border: "1px solid #FCD34D",
							borderRadius: 8,
							padding: 16,
							width: "100%",
							marginBottom: 16,
							display: "flex",
							alignItems: "flex-start",
							gap: 12
						}}
					>
						<ErrorOutlineIcon style={{ color: "#D97706", fontSize: 24, marginTop: 2 }} />
						<Box textAlign="left">
							<Typography
								variant="subtitle2"
								style={{
									color: "#92400E",
									fontWeight: 600,
									marginBottom: 4
								}}
							>
								System Detection
							</Typography>
							<Typography variant="body2" style={{ color: "#92400E", lineHeight: 1.5 }}>
								We detected that the <strong>{idType}</strong> matches with our system records.
							</Typography>
						</Box>
					</Paper>

					{/* Detailed Message */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#F9FAFB",
							borderRadius: 8,
							padding: 20,
							width: "100%",
							borderLeft: "4px solid #D97706",
							marginBottom: 16,
							textAlign: "left"
						}}
					>
						<Typography
							variant="body1"
							style={{
								color: "#374151",
								lineHeight: 1.6,
								fontWeight: 500,
								marginBottom: 12
							}}
						>
							You are an <strong>existing employee</strong> in our system. You cannot proceed with
							creating a new profile using this {idType}.
						</Typography>

						<Divider style={{ margin: "12px 0" }} />

						<Typography
							variant="body2"
							style={{
								color: "#6B7280",
								lineHeight: 1.5
							}}
						>
							Please verify your information and contact your HR administrator if you believe this is an
							error or need to update your existing records.
						</Typography>
					</Paper>

					{/* Info Details - Shows SSN or ALT ID */}
					<Paper
						elevation={0}
						style={{
							backgroundColor: "#EFF6FF",
							borderRadius: 6,
							padding: "12px 16px",
							width: "100%",
							border: "1px solid #BFDBFE",
							display: "flex",
							alignItems: "center"
						}}
					>
						<VerifiedUserIcon style={{ color: "#3B82F6", marginRight: 12, fontSize: 20 }} />
						<Box textAlign="left" flex={1}>
							<Typography variant="caption" style={{ color: "#6B7280", display: "block" }}>
								{idLabel}
							</Typography>
							<Typography
								variant="body2"
								style={{
									color: "#1E40AF",
									fontWeight: 600,
									fontFamily: "monospace",
									fontSize: "0.95rem"
								}}
							>
								{displayValue}
							</Typography>
						</Box>
						<InfoIcon style={{ color: "#93C5FD", fontSize: 18 }} />
					</Paper>
				</Box>
			</DialogContent>

			<DialogActions
				style={{
					padding: "16px 24px 24px 24px",
					justifyContent: "center"
				}}
			>
				<Button
					onClick={onClose}
					variant="contained"
					size="large"
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					style={{
						backgroundColor: isHovered ? "#1F2937" : "#111827",
						color: "white",
						textTransform: "none",
						fontWeight: 600,
						padding: "10px 32px",
						borderRadius: 8,
						boxShadow: isHovered
							? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
							: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
						transition: "all 0.2s ease",
						minWidth: 140
					}}
				>
					Understood
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

export default ExistsOwnMemberModel;
